import { Injectable, isDevMode, Injector } from "@angular/core";
import { User } from "./user";
import { HttpClient } from "@angular/common/http";
import { ReplaySubject, timer } from "rxjs";
// ProgressService removed to fix circular dependency - using lazy injection via Injector
import { Router } from '@angular/router';
import { AnalyticsService } from "./analytics.service";
import { AlertController, Platform, ToastController } from '@ionic/angular';
import { Onboarding } from "./onboarding";
import { User as fbAuthUser, getAuth, EmailAuthProvider, reauthenticateWithCredential, updateEmail, deleteUser } from "firebase/auth";
import { filter, take } from "rxjs/operators";
import { FirebaseInitService } from "./firebase-init.service";
import { getApp } from "firebase/app";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

/**
 * Service that handles other user-related actions within our own backend.
 */
@Injectable({
    providedIn: "root"
})
export class UserService {
    public user?: User;
    public user$ = new ReplaySubject<User | null | { role: "anon" }>(1);
    
    public clearUser(): void {
      this.user = undefined;
      this.user$.next(null);
    }

    public fireUser?: fbAuthUser;

    private get spinnerService(): any {
        return null; // Spinner removed to fix circular dependency
    }

    private _progressService: any = null;
    private async getProgressServiceAsync(): Promise<any> {
        if (!this._progressService) {
            // Dynamically import to avoid circular dependency at module initialization
            const module = await import('./progress.service');
            const ProgressServiceClass = module.ProgressService;
            this._progressService = this.injector.get(ProgressServiceClass);
        }
        return this._progressService;
    }

    constructor(
        public httpClient: HttpClient, 
        private injector: Injector,
        public readonly router: Router,
        public analyticsService: AnalyticsService,
        public platform: Platform,
        private toastController: ToastController,
        private alertController: AlertController,
        private firebaseInit: FirebaseInitService
        ) {
        if (isDevMode()) {
            (window as any).userService = this;
        }
     }

    isLoggedIn(user: User): user is Exclude<User, { role: 'anon' } | null> {
        return !!user && user.role !== 'User';
    }
  
    isTeacher(user: User): boolean {
        return !!user && user.role === 'Teacher';
    }
  
    /**
     * Checks if the user with this username exists in database.
     * @returns boolean if the username is already taken.
    */
    public async doesUserExist(uid: string): Promise<boolean> {
        return await this.httpClient.get<boolean>(`user/${uid}`).toPromise();
    }

    /**
     * Checks if the user with this username exists.
     * @returns boolean if the username is already taken.
     */
    public async doesUsernameExist(username: string): Promise<boolean> {
        try {
            const result = await this.httpClient.get<boolean>(`user/username/${username}`).toPromise();
            return result === true; // Ensure we return a strict boolean
        } catch (error: any) {
            console.error(`📱 [UserService] doesUsernameExist("${username}") error:`, error?.message || error);
            console.error(`📱 [UserService] Error details - status: ${error?.status}, statusText: ${error?.statusText}`);
            throw error;
        }
    }

    /**
     * Checks if a user with this email exists.
     * @returns boolean if the email is already taken.
     */
    /*public async doesEmailExist(username: string): Promise<boolean> {
        return await this.httpClient.get<boolean>(`user/email/${username}`).toPromise();
    }*/

    /**
     * Creates a user in out custom backend.
     * @param user - The firebase-user to create a user-object for.
     * @param username - the username this user should have.
     */
    public async createUserInBackend(user: fbAuthUser, username: string, age: number, onboarding?: Onboarding, promocode?: string /*fname: string, lname: string,*/): Promise<User> {
        const dbUser = await this.httpClient.post<User>("user",
            { uid: user.uid, username, age: age, photoURL: user.photoURL, role: "User", points: 0, lifes: 10, onboarding: onboarding, promocode: promocode}).toPromise();
        await this.setCurrentUser(dbUser);
        return dbUser;
    }

    /**
     * Sets the current user for the app.
     * @param user - the signed-in user to grab the database object for or the database object itself.
     */
    public async setCurrentUser(user: fbAuthUser | User | null): Promise<void> {
        try {
            if (!user) {
                this.user = undefined;
                this.user$.next(undefined);
                return;
              }
              if ("isAnonymous" in user && user.isAnonymous) {
                this.user$.next({ role: "anon" });
                return;
              }
              if ("username" in user) {
                this.user = user;
                this.user$.next(this.user);
                return;
              }
            
              console.log("SET USER USERSERVICE", user.uid);
            
              try {
                await this.spinnerService?.presentLoading();
              } catch {}
            
              try {
                const dbUser = await this.httpClient.get<User>(`user/${user.uid}`).toPromise();
            
                if (dbUser) {
                  this.user = dbUser;
                  console.log("User fetched:", dbUser);
                  this.user$.next(this.user);
                  console.log("✅ User emitted to user$ observable");
                  // Skills + Analytics
                  this.setSkills().catch(err => console.error("Error loading skills:", err));
                  await this.analyticsService.setIdentity(this.user.uid);
                  await this.analyticsService.branchSetReqMetData();
                  console.log("User setup complete:", dbUser);
                } else {
                  console.warn("⚠️ No dbUser returned from backend for uid", user.uid);
                  this.user = undefined;
                  this.user$.next(undefined);
                }
              } catch (err) {
                console.error("❌ Error fetching dbUser in setCurrentUser:", err);
                
                // Clear user state
                this.user = undefined;
                this.user$.next(undefined);
                
                // Re-throw the error so the calling code (e.g., login page) can handle it
                // The login page will show a toast with the server message
                throw err;
              } finally {
                try {
                  await this.spinnerService?.dismissLoading();
                } catch {}
              }
        } catch (err) {
            console.error("❌ Error setting current user in setCurrentUser:", err);
        }
        }
      

    public async getUserApi(): Promise<User>
    {
        return await this.httpClient.get<User>(`user`).toPromise();
    }

    public async getFireUser(): Promise<any>
    {
        try {
            // First check if we have a user from AuthService (works on iOS native)
            // This is set after login via native plugin
            const authService = (window as any).__authService;
            if (authService?.fireUser) {
                console.log('📱 [UserService] getFireUser: Using AuthService.fireUser');
                return authService.fireUser;
            }
            
            // Fallback to Web SDK (works on web)
            const auth = getAuth();
            const user = auth.currentUser;
            
            if (user) {
                return user;
            }
            
            return null;
        } catch (error) {
            console.error("Error getting Firebase user:", error);
            return null;
        }
    }

    public async getUser(): Promise<User | undefined> {
        return this.user;
    }

    public async getAllUsers(): Promise<Array<User>>
    {
        return await this.httpClient.get<Array<User>>(`user/all`).toPromise();
    }

    public async setSkills(): Promise<void>
    {
        const progressService = await this.getProgressServiceAsync();
        if (progressService) {
            let promise = progressService.getSkillsByUid()
                .then(function data(skills) {
                  return skills;
                });
            this.user!.skills = await promise;
        }
    }
    
    /**
     * Updates the user in our database.
     */
    public async updateUser(userUpdate: Partial<User>, fireUser?: fbAuthUser): Promise<User | void> {
        if (fireUser || this.user) {
            return await this.httpClient.put<User>(`user/${(fireUser! || this.user).uid}`, userUpdate).toPromise();
        }
        console.log("User is not available " + userUpdate);
    }

    public async updateEmail(email: string, currentPassword?: string): Promise<void> {
        try {
            const auth = getAuth();
            const user = auth.currentUser;
            
            if (!user) {
                throw new Error("No user logged in");
            }

            // Re-authenticate user first (Firebase requires this) only if password provided
            if (currentPassword && user.email) {
                const credential = EmailAuthProvider.credential(user.email, currentPassword);
                await reauthenticateWithCredential(user, credential);
            }

            // Update email
            await updateEmail(user, email);
            
            console.log("Email updated successfully");
        } catch (error) {
            console.error("Error updating email:", error);
            throw error;
        }
    }

    /**
    * Delete user from database (users + skills collections)
    */
    public async deleteUserFromDB(): Promise<User>
    {
        return await this.httpClient.delete<User>(`user`).toPromise();
    }

    /**
    * Initiate a permanently deletion of an account from Firebase & database
    */
    public async deleteAccount(): Promise<void> {
        try {
          // Sicherheitshalber warten, bis Firebase "ready" ist
          await this.firebaseInit.waitForReady();
      
          const user = this.fireUser;
          if (!user) {
            console.error("No user to delete");
            return;
          }
      
          const isNative = this.platform.is('capacitor') || this.platform.is('cordova');
      
          // 1️⃣ Erst Backend-User löschen (falls nicht anonym)
          if (!user.isAnonymous) {
            try {
              await this.deleteUserFromDB();   // deine bestehende Methode
            } catch (dbErr) {
              console.error("Error deleting user from DB:", dbErr);
              // Wenn hier was schief geht, lieber abbrechen,
              // damit Firebase- und Backend-Status nicht auseinanderlaufen
              throw dbErr;
            }
          }
      
          // 2️⃣ Firebase-Account löschen
          if (isNative) {
            console.log("📱 [AuthService] deleteAccount → native FirebaseAuthentication.deleteUser()");
            await FirebaseAuthentication.deleteUser();
          } else {
            console.log("🌐 [UserService] deleteAccount → web deleteUser()");
            await this.firebaseInit.waitForReady();
            const auth = getAuth(getApp());
            const current = auth.currentUser;
      
            if (!current) {
              console.warn("No auth.currentUser, using fireUser as fallback");
            } else if (current.uid !== user.uid) {
              console.warn("auth.currentUser uid != fireUser uid, deleting currentUser anyway", {
                authUid: current.uid,
                fireUid: user.uid
              });
            }
      
            await deleteUser(current || user);
          }
      
          // 3️⃣ Analytics & SignOut-Tracking
          try {
            await this.analyticsService.trackEvent("Delete Account");
            await this.analyticsService.signOut();
          } catch (analyticsErr) {
            console.warn("Analytics error on deleteAccount:", analyticsErr);
          }
      
          // 4️⃣ Lokalen State aufräumen
          this.clearUser();
          this.fireUser = undefined;
      
          localStorage.removeItem("onboarding");
          localStorage.removeItem("authToken");
      
          // 5️⃣ Zur Startseite
          this.router.navigate(["start"], { replaceUrl: true });
      
        } catch (err: any) {
          console.error("Error deleting account:", err);
      
          const code = err?.code || err?.error?.code;
          if (code === "auth/requires-recent-login" || code === "requires-recent-login") {
            // User muss sich vorher neu einloggen
            this.router.navigateByUrl("login/reauth");
          } else {
            this.showToast("Konto konnte nicht gelöscht werden. Bitte versuche es später erneut.");
          }
        }
      }
      
    /**
    * Checks if user exists in firestore
    */
    public async doesFireUserExist(user: fbAuthUser): Promise<boolean>
    {
        try {
            // Check if user has valid uid
            if (!user || !user.uid) {
                return false;
            }

            // Try to get additional info to verify user exists
            // If user is valid, it will have a uid
            const userId = user.uid;
            
            // Check if it's not a deleted or invalid user
            // Deleted users typically have a uid starting with "DELETED_"
            if (userId.startsWith("DELETED_")) {
                return false;
            }

            // User exists if it has a valid uid
            return true;
        } catch (error) {
            console.error("Error checking if Firebase user exists:", error);
            return false;
        }
    }

    public isAdmin(): boolean {
        return this.user?.role === "Admin";
    }

    public getPromocode(): string {
        return this.user!.promocode; 
    }

    public async showToast(message?: string): Promise<void> {
        let toast = await this.toastController.create({
            message: message ?? "Ein Fehler ist aufgetreten.",
            duration: 3000,
            animated: true,
            position: "bottom",
        });
        await toast.present();
    }

    public async showLicenseBlockedAlert(): Promise<void> {
        const alert = await this.alertController.create({
            header: 'Kein aktiver Zugang',
            message: 'Dein Account ist vorübergehend deaktiviert, da keine Lizenz verfügbar ist. Bitte wende dich an deine Lehrkraft, damit wieder ein Platz für dich freigegeben werden kann.',
            buttons: [
                {
                    text: 'OK',
                    handler: () => {
                        // Sign out the user
                        this.clearUser();
                        this.router.navigate(['start'], { replaceUrl: true });
                    }
                }
            ],
            backdropDismiss: false
        });
        await alert.present();
    }

    public async updateEduplacesUser(eduplacesUser: any) {
        // Emit a value when `this.user` is available and proceed with the update
        const userCheck$ = timer(0, 100).pipe(
            filter(() => !!this.user), // Check if `this.user` is truthy
            take(1) // Take the first value once `this.user` is set
        );

        userCheck$.subscribe(async ()  => {
            const schoolId = eduplacesUser.schoolId ?? eduplacesUser.school_id;
            const schoolName = eduplacesUser.schoolName ?? eduplacesUser.school_name;
            if (!schoolId || !schoolName) {
                console.warn("[updateEduplacesUser] Missing school id or name, skipping update.", {
                    schoolId,
                    schoolName
                });
                return;
            }

            const type = eduplacesUser.type ?? eduplacesUser.schoolType ?? eduplacesUser.school_type;
            const schoolObj: User["school"] = {
                school_id: schoolId,
                school_name: schoolName,
                school_official_id: eduplacesUser.schoolOfficialId ?? eduplacesUser.school_official_id,
                school_type: type,
                role: eduplacesUser.schoolRole ?? eduplacesUser.role,
                profile: eduplacesUser.schoolProfile ?? eduplacesUser.profile,
                groups: eduplacesUser.schoolGroups
            };
            
            const user: Partial<User> = { school: schoolObj };
            await this.updateUser(user);
        });
    }
}